import type { InlineConfig } from 'vite';
import tsconfigPaths from '@wix/vite-tsconfig-paths';
import { viteExternalsPlugin } from 'vite-plugin-externals';
import { BROWSER_VITE_CACHE_DIR } from '@wix/cli-app-definitions';
import {
  DASHBOARD_PAGES_PATTERN,
  DASHBOARD_PLUGINS_PATTERN,
  DASHBOARD_MODALS_PATTERN,
} from '../../../dev-center/components/dashboard/components.js';
import type { ProjectModel } from '../../../model.js';
import { inlineHtml } from '../../plugins/inline-html.js';
import { moduleBundleDev } from '../../plugins/module-bundle/plugin.js';
import type { ViteDevConfigOptions } from '../../vite-config-options.js';
import { ConfigType } from '../../vite-config-options.js';
import { draftJs } from '../../plugins/draft-js.js';
import { ssl } from '../../plugins/ssl.js';
import { hmr } from '../../plugins/hmr/plugin.js';
import { autoOrigin } from '../../plugins/auto-origin.js';
import { embeddedScriptSDKSupport } from '../../plugins/embedded-script-sdk-support.js';
import {
  SITE_WIDGETS_ELEMENT_PATTERN,
  SITE_WIDGETS_PANEL_PATTERN,
  SITE_PLUGINS_PLUGIN_PATTERN,
  SITE_PLUGINS_PANEL_PATTERN,
  BLOCKS_WIDGETS_PATTERN,
  BLOCKS_WIDGETS_PANELS_PATTERN,
  SITE_COMPONENTS_PANEL_PATTERN,
} from '../../../dev-center/components/site/components.js';
import { monitoring } from '../../plugins/monitoring.js';
import { fbjsHotfixPlugin } from '../../plugins/fbjs-hotfix-plugin.js';
import { webMethodsPlugin } from '../../plugins/web-methods-plugin.js';

export function getViteConfig(
  model: ProjectModel,
  options: ViteDevConfigOptions
): InlineConfig {
  const { https, baseBackendUrl, port, origin } = options;

  const allowedHosts = origin ? [new URL(origin).hostname] : undefined;

  return {
    configFile: false,
    root: model.projectFolder,
    customLogger: options.customLogger,
    clearScreen: false,
    logLevel: 'info',
    publicDir: false,
    cacheDir: BROWSER_VITE_CACHE_DIR,
    optimizeDeps: {
      include: ['@wix/sdk-context'],
      entries: [
        DASHBOARD_PAGES_PATTERN,
        DASHBOARD_PLUGINS_PATTERN,
        DASHBOARD_MODALS_PATTERN,
        SITE_WIDGETS_ELEMENT_PATTERN,
        SITE_WIDGETS_PANEL_PATTERN,
        SITE_PLUGINS_PLUGIN_PATTERN,
        SITE_PLUGINS_PANEL_PATTERN,
        BLOCKS_WIDGETS_PATTERN,
        SITE_COMPONENTS_PANEL_PATTERN,
        BLOCKS_WIDGETS_PANELS_PATTERN,
      ],
    },
    server: {
      cors: { origin: true },
      port,
      strictPort: !!port,
      host: !!origin,
      allowedHosts,
      origin,
    },
    define: {
      ...(baseBackendUrl
        ? {
            'import.meta.env.BASE_API_URL': JSON.stringify(
              `${baseBackendUrl.replace(/\/$/, '')}/functions`
            ),
          }
        : {}),
    },
    plugins: [
      webMethodsPlugin({
        projectFolder: model.projectFolder,
      }),
      fbjsHotfixPlugin(),
      draftJs(),
      ...(https ? [ssl()] : []),
      moduleBundleDev({
        initialModel: model,
        projectModelWatcher: options.projectModelWatcher,
      }),
      hmr(),
      viteExternalsPlugin({
        react: 'React',
        'react-dom': 'ReactDOM',
      }),
      inlineHtml({
        type: ConfigType.dev,
        initialModel: model,
        ...options,
      }),
      autoOrigin(),
      embeddedScriptSDKSupport({
        type: ConfigType.dev,
        initialModel: model,
        ...options,
      }),
      monitoring({ initialModel: model }),
      tsconfigPaths({
        ignoreConfigErrors: true,
        root: model.projectFolder,
      }),
    ],
  };
}
